import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { useRealtimeKey } from '@/hooks/useRealtimeManager';
import type {
  Company,
  CompanyWithStats,
  CompanyDataStats,
  CompanyMemberDetailed,
  ActiveImpersonationSession,
  UserDeletionPreview,
  PlatformStats,
  SuperAdminAuditLog,
  AuditLogStats,
} from '@shared/types';

// ============================================================================
// Query: Get All Companies with Stats
// ============================================================================

/**
 * Get all companies across the platform with member counts (Super admin only)
 */
export function useAllCompanies() {
  return useQuery({
    queryKey: ['superadmin', 'companies'],
    queryFn: async (): Promise<CompanyWithStats[]> => {
      const { data, error } = await supabase.rpc('get_all_companies_with_stats');

      if (error) {
        console.error('Error fetching all companies:', error);
        throw error;
      }

      return data || [];
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// ============================================================================
// Query: Get Company By ID
// ============================================================================

/**
 * Get a specific company by ID (Super admin only)
 * Uses SECURITY DEFINER function to bypass RLS
 */
export function useCompanyById(companyId: string | undefined) {
  return useQuery({
    queryKey: ['superadmin', 'company', companyId],
    queryFn: async (): Promise<Company | null> => {
      if (!companyId) {
        throw new Error('Company ID is required');
      }

      const { data, error } = await supabase.rpc('get_company_by_id', {
        p_company_id: companyId,
      });

      if (error) {
        console.error('Error fetching company by ID:', error);
        throw error;
      }

      // RPC returns array, get first item or null
      if (Array.isArray(data) && data.length > 0) {
        const company = data[0];
        // Transform to camelCase
        return {
          id: company.id,
          name: company.name,
          logoUrl: company.logo_url,
          contactEmail: company.contact_email,
          contactPhone: company.contact_phone,
          createdAt: company.created_at,
          updatedAt: company.updated_at,
        };
      }

      return null;
    },
    enabled: !!companyId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// ============================================================================
// Query: Get Company Data Stats
// ============================================================================

/**
 * Get detailed data counts for a specific company (Super admin only)
 */
export function useCompanyDataStats(companyId: string | undefined) {
  return useQuery({
    queryKey: ['superadmin', 'company-stats', companyId],
    queryFn: async (): Promise<CompanyDataStats> => {
      if (!companyId) {
        throw new Error('Company ID is required');
      }

      const { data, error } = await supabase.rpc('get_company_data_stats', {
        p_company_id: companyId,
      });

      if (error) {
        console.error('Error fetching company stats:', error);
        throw error;
      }

      // RPC returns array, get first item
      return Array.isArray(data) && data.length > 0
        ? data[0]
        : {
            organizations_count: 0,
            contacts_count: 0,
            deals_count: 0,
            activities_count: 0,
            tasks_count: 0,
            members_count: 0,
          };
    },
    enabled: !!companyId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// ============================================================================
// Query: Get Company Members Detailed
// ============================================================================

/**
 * Get all members of a company with detailed information (Super admin only)
 */
export function useCompanyMembers(companyId: string | undefined) {
  return useQuery({
    queryKey: ['superadmin', 'company-members', companyId],
    queryFn: async (): Promise<CompanyMemberDetailed[]> => {
      if (!companyId) {
        throw new Error('Company ID is required');
      }

      const { data, error } = await supabase.rpc('get_company_members_detailed', {
        p_company_id: companyId,
      });

      if (error) {
        console.error('Error fetching company members:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!companyId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// ============================================================================
// Query: Get Platform-Wide Statistics
// ============================================================================

/**
 * Get platform-wide statistics (Super admin only)
 */
export function usePlatformStats() {
  return useQuery({
    queryKey: ['superadmin', 'platform-stats'],
    queryFn: async (): Promise<PlatformStats> => {
      const { data, error } = await supabase.rpc('get_platform_stats');

      if (error) {
        console.error('Error fetching platform stats:', error);
        throw error;
      }

      // RPC returns array, get first item
      return Array.isArray(data) && data.length > 0
        ? data[0]
        : {
            total_companies: 0,
            total_users: 0,
            total_organizations: 0,
            total_contacts: 0,
            total_deals: 0,
            total_activities: 0,
            active_impersonations: 0,
          };
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// ============================================================================
// Query: Get User Deletion Preview
// ============================================================================

/**
 * Get user deletion preview data (Super admin only)
 */
export function useUserDeletionPreview(userId: string | undefined) {
  return useQuery({
    queryKey: ['superadmin', 'user-deletion-preview', userId],
    queryFn: async (): Promise<UserDeletionPreview> => {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const { data, error } = await supabase.rpc('prepare_user_deletion', {
        p_user_id: userId,
      });

      if (error) {
        console.error('Error fetching user deletion preview:', error);
        throw error;
      }

      // RPC returns array, get first item
      return Array.isArray(data) && data.length > 0
        ? data[0]
        : {
            user_exists: false,
            user_name: null,
            user_email: null,
            is_company_owner: false,
            companies: null,
            data_stats: null,
          };
    },
    enabled: !!userId,
    staleTime: 0, // Always fresh for deletion operations
  });
}

// ============================================================================
// Query: Get Active Impersonation Session
// ============================================================================

/**
 * Get the current active impersonation session (Super admin only)
 */
export function useActiveImpersonationSession() {
  const { isIdle } = useRealtimeKey();

  return useQuery({
    queryKey: ['superadmin', 'active-impersonation'],
    queryFn: async (): Promise<ActiveImpersonationSession | null> => {
      const { data, error } = await supabase.rpc('get_active_impersonation_session');

      if (error) {
        console.error('Error fetching active impersonation session:', error);
        throw error;
      }

      // RPC returns array, get first item or null
      return Array.isArray(data) && data.length > 0 ? data[0] : null;
    },
    staleTime: 1000 * 30, // 30 seconds
    refetchInterval: isIdle ? false : 1000 * 60,
  });
}

// ============================================================================
// Query: Get Super Admin Audit Logs
// ============================================================================

/**
 * Get super admin audit logs (Super admin only)
 */
export function useSuperAdminAuditLogs(days: number = 30) {
  return useQuery({
    queryKey: ['superadmin', 'audit-logs', days],
    queryFn: async (): Promise<SuperAdminAuditLog[]> => {
      const { data, error } = await supabase
        .from('super_admin_audit_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) {
        console.error('Error fetching audit logs:', error);
        throw error;
      }

      return data || [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// ============================================================================
// Query: Get Super Admin Audit Stats
// ============================================================================

/**
 * Get super admin audit statistics (Super admin only)
 */
export function useSuperAdminAuditStats(days: number = 30) {
  return useQuery({
    queryKey: ['superadmin', 'audit-stats', days],
    queryFn: async (): Promise<AuditLogStats[]> => {
      const { data, error } = await supabase.rpc('get_super_admin_audit_stats', {
        p_days: days,
      });

      if (error) {
        console.error('Error fetching audit stats:', error);
        throw error;
      }

      return data || [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// ============================================================================
// Mutation: Start Impersonation
// ============================================================================

/**
 * Start impersonating a user (Super admin only)
 */
export function useStartImpersonation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      userId: string;
      userName: string;
      companyId?: string;
      companyName?: string;
    }): Promise<ActiveImpersonationSession> => {
      const { userId, userName, companyId, companyName } = params;

      // Generate unique session token
      const sessionToken = crypto.randomUUID();

      // Get super admin ID
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create impersonation session
      const { data: session, error: sessionError } = await supabase
        .from('impersonation_sessions')
        .insert({
          super_admin_id: user.id,
          impersonated_user_id: userId,
          session_token: sessionToken,
          expires_at: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString(), // 8 hours
        })
        .select()
        .single();

      if (sessionError) throw sessionError;

      // Log impersonation start
      await supabase.rpc('log_super_admin_action', {
        p_action: 'impersonate_start',
        p_target_type: 'user',
        p_target_id: userId,
        p_target_name: userName,
        p_details: {
          session_id: session.id,
          company_id: companyId,
          company_name: companyName,
        },
      });

      return {
        id: session.id,
        super_admin_id: session.super_admin_id,
        impersonated_user_id: session.impersonated_user_id,
        session_token: session.session_token,
        started_at: session.started_at,
        expires_at: session.expires_at,
      };
    },
    onSuccess: () => {
      // Invalidate superadmin queries
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'active-impersonation'] });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'audit-logs'] });

      // Clear ALL React Query cache to force refetch with new impersonation context
      // This ensures useCurrentCompany() and all other hooks fetch fresh data
      // for the impersonated user (not the cached super admin data)
      queryClient.clear();
    },
  });
}

// ============================================================================
// Mutation: End Impersonation
// ============================================================================

/**
 * End the current impersonation session (Super admin only)
 */
export function useEndImpersonation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: string): Promise<void> => {
      // Get session details for logging
      const { data: session, error: sessionError } = await supabase
        .from('impersonation_sessions')
        .select('*')
        .eq('id', sessionId)
        .single();

      if (sessionError) throw sessionError;

      // End the session
      const { error: updateError } = await supabase
        .from('impersonation_sessions')
        .update({
          ended_at: new Date().toISOString(),
        })
        .eq('id', sessionId);

      if (updateError) throw updateError;

      // Log impersonation end
      await supabase.rpc('log_super_admin_action', {
        p_action: 'impersonate_end',
        p_target_type: 'impersonation_session',
        p_target_id: sessionId,
        p_target_name: null,
        p_details: {
          impersonated_user_id: session.impersonated_user_id,
          duration_minutes: Math.round(
            (new Date().getTime() - new Date(session.started_at).getTime()) / 1000 / 60
          ),
        },
      });
    },
    onSuccess: () => {
      // Invalidate superadmin queries
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'active-impersonation'] });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'audit-logs'] });

      // Clear ALL React Query cache to return to super admin's own data
      // This ensures all hooks fetch fresh data for the super admin
      // (not the cached impersonated user data)
      queryClient.clear();
    },
  });
}

// ============================================================================
// Mutation: Log Super Admin Action
// ============================================================================

/**
 * Log a super admin action to the audit log
 */
export function useLogSuperAdminAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      action,
      targetType,
      targetId,
      targetName,
      details,
    }: {
      action: string;
      targetType?: string;
      targetId?: string;
      targetName?: string;
      details?: Record<string, unknown>;
    }): Promise<string> => {
      const { data, error } = await supabase.rpc('log_super_admin_action', {
        p_action: action,
        p_target_type: targetType || null,
        p_target_id: targetId || null,
        p_target_name: targetName || null,
        p_details: details || {},
      });

      if (error) throw error;

      return data; // Returns log ID
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'audit-logs'] });
    },
  });
}

// ============================================================================
// Helper: Check if Current User is Super Admin
// ============================================================================

/**
 * Check if the current user is a super admin
 */
export function useIsSuperAdmin() {
  return useQuery({
    queryKey: ['auth', 'is-super-admin'],
    queryFn: async (): Promise<boolean> => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) return false;

      const { data, error } = await supabase.rpc('is_super_admin');

      if (error) {
        console.error('Error checking super admin status:', error);
        return false;
      }

      return data || false;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// ============================================================================
// BILLING MANAGEMENT (Superadmin Only)
// ============================================================================

/**
 * Subscription plan from database (snake_case for direct DB mapping)
 */
export interface SubscriptionPlanDB {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  stripe_product_id: string | null;
  // EUR Stripe prices (default)
  stripe_price_id_monthly: string | null;
  stripe_price_id_yearly: string | null;
  // USD Stripe prices (Issue #479)
  stripe_price_id_monthly_usd: string | null;
  stripe_price_id_yearly_usd: string | null;
  // GBP Stripe prices (Issue #479)
  stripe_price_id_monthly_gbp: string | null;
  stripe_price_id_yearly_gbp: string | null;
  // EUR display prices
  price_monthly_cents: number;
  price_yearly_cents: number;
  // USD display prices (Issue #479)
  price_monthly_cents_usd: number;
  price_yearly_cents_usd: number;
  // GBP display prices (Issue #479)
  price_monthly_cents_gbp: number;
  price_yearly_cents_gbp: number;
  currency: string;
  max_users: number | null;
  max_contacts: number | null;
  max_deals: number | null;
  max_organizations: number | null;
  max_email_accounts: number | null;
  max_workflows: number | null;
  max_storage_gb: number | null;
  max_projects: number | null;
  max_brand_voices: number | null;
  features: Record<string, boolean>;
  included_addons: string[];
  is_active: boolean;
  is_featured: boolean;
  display_order: number;
  badge_text: string | null;
  // API settings
  api_enabled: boolean;
  api_rate_limit_per_minute: number;
  api_daily_limit: number;
  api_bulk_enabled: boolean;
  api_max_keys: number;
  created_at: string;
  updated_at: string;
}

/**
 * Company subscription from database (snake_case for direct DB mapping)
 */
export interface CompanySubscriptionDB {
  id: string;
  company_id: string;
  company_name: string;
  plan_id: string;
  plan_name: string;
  plan_slug: string;
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid' | 'paused';
  billing_cycle: 'monthly' | 'yearly';
  quantity: number;
  trial_start: string | null;
  trial_end: string | null;
  grandfathered: boolean;
  cancel_at_period_end: boolean;
  current_period_end: string | null;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// Query: Get All Subscription Plans (Superadmin Only)
// ============================================================================

/**
 * Get all subscription plans for superadmin management
 */
export function useAllSubscriptionPlans() {
  return useQuery({
    queryKey: ['superadmin', 'subscription-plans'],
    queryFn: async (): Promise<SubscriptionPlanDB[]> => {
      const { data, error } = await supabase.rpc('get_all_subscription_plans');

      if (error) {
        console.error('Error fetching subscription plans:', error);
        throw error;
      }

      return data || [];
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// ============================================================================
// Mutation: Create Subscription Plan (Superadmin Only)
// ============================================================================

export interface CreatePlanParams {
  name: string;
  slug: string;
  description?: string;
  price_monthly_cents?: number;
  price_yearly_cents?: number;
  currency?: string;
  max_users?: number | null;
  max_contacts?: number | null;
  max_deals?: number | null;
  max_organizations?: number | null;
  max_email_accounts?: number | null;
  max_workflows?: number | null;
  max_storage_gb?: number | null;
  max_projects?: number | null;
  max_brand_voices?: number | null;
  features?: Record<string, boolean>;
  included_addons?: string[];
  is_active?: boolean;
  is_featured?: boolean;
  display_order?: number;
  badge_text?: string;
  // API settings
  api_enabled?: boolean;
  api_rate_limit_per_minute?: number;
  api_daily_limit?: number;
  api_bulk_enabled?: boolean;
  api_max_keys?: number;
}

export function useCreatePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreatePlanParams): Promise<string> => {
      const { data, error } = await supabase.rpc('admin_create_plan', {
        p_name: params.name,
        p_slug: params.slug,
        p_description: params.description || null,
        p_price_monthly_cents: params.price_monthly_cents || 0,
        p_price_yearly_cents: params.price_yearly_cents || 0,
        p_currency: params.currency || 'EUR',
        p_max_users: params.max_users,
        p_max_contacts: params.max_contacts,
        p_max_deals: params.max_deals,
        p_max_organizations: params.max_organizations,
        p_max_email_accounts: params.max_email_accounts,
        p_max_workflows: params.max_workflows,
        p_max_storage_gb: params.max_storage_gb,
        p_max_projects: params.max_projects,
        p_max_brand_voices: params.max_brand_voices,
        p_features: params.features || { emailSync: false, calendarSync: false, workflows: false, aiFeatures: false, calling: false },
        p_included_addons: params.included_addons || [],
        p_is_active: params.is_active ?? true,
        p_is_featured: params.is_featured ?? false,
        p_display_order: params.display_order ?? 0,
        p_badge_text: params.badge_text || null,
        // API settings
        p_api_enabled: params.api_enabled ?? false,
        p_api_rate_limit_per_minute: params.api_rate_limit_per_minute ?? 60,
        p_api_daily_limit: params.api_daily_limit ?? 1000,
        p_api_bulk_enabled: params.api_bulk_enabled ?? false,
        p_api_max_keys: params.api_max_keys ?? 5,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'subscription-plans'] });
    },
  });
}

// ============================================================================
// Mutation: Update Subscription Plan (Superadmin Only)
// ============================================================================

export interface UpdatePlanParams {
  id: string;
  name?: string;
  description?: string;
  price_monthly_cents?: number;
  price_yearly_cents?: number;
  max_users?: number | null;
  max_contacts?: number | null;
  max_deals?: number | null;
  max_organizations?: number | null;
  max_email_accounts?: number | null;
  max_workflows?: number | null;
  max_storage_gb?: number | null;
  max_projects?: number | null;
  max_brand_voices?: number | null;
  features?: Record<string, boolean>;
  included_addons?: string[];
  is_active?: boolean;
  is_featured?: boolean;
  display_order?: number;
  badge_text?: string | null;
  // API settings
  api_enabled?: boolean;
  api_rate_limit_per_minute?: number;
  api_daily_limit?: number;
  api_bulk_enabled?: boolean;
  api_max_keys?: number;
}

export function useUpdatePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdatePlanParams): Promise<boolean> => {
      const { data, error } = await supabase.rpc('admin_update_plan', {
        p_plan_id: params.id,
        p_name: params.name,
        p_description: params.description,
        p_price_monthly_cents: params.price_monthly_cents,
        p_price_yearly_cents: params.price_yearly_cents,
        p_max_users: params.max_users,
        p_max_contacts: params.max_contacts,
        p_max_deals: params.max_deals,
        p_max_organizations: params.max_organizations,
        p_max_email_accounts: params.max_email_accounts,
        p_max_workflows: params.max_workflows,
        p_max_storage_gb: params.max_storage_gb,
        p_max_projects: params.max_projects,
        p_max_brand_voices: params.max_brand_voices,
        p_features: params.features,
        p_included_addons: params.included_addons,
        p_is_active: params.is_active,
        p_is_featured: params.is_featured,
        p_display_order: params.display_order,
        p_badge_text: params.badge_text,
        // API settings
        p_api_enabled: params.api_enabled,
        p_api_rate_limit_per_minute: params.api_rate_limit_per_minute,
        p_api_daily_limit: params.api_daily_limit,
        p_api_bulk_enabled: params.api_bulk_enabled,
        p_api_max_keys: params.api_max_keys,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'subscription-plans'] });
    },
  });
}

// ============================================================================
// Mutation: Delete Subscription Plan (Superadmin Only)
// ============================================================================

export function useDeletePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (planId: string): Promise<boolean> => {
      const { data, error } = await supabase.rpc('admin_delete_plan', {
        p_plan_id: planId,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'subscription-plans'] });
    },
  });
}

// ============================================================================
// Query: Get All Company Subscriptions (Superadmin Only)
// ============================================================================

export function useAllCompanySubscriptions() {
  return useQuery({
    queryKey: ['superadmin', 'company-subscriptions'],
    queryFn: async (): Promise<CompanySubscriptionDB[]> => {
      const { data, error } = await supabase.rpc('get_all_company_subscriptions');

      if (error) {
        console.error('Error fetching company subscriptions:', error);
        throw error;
      }

      return data || [];
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// ============================================================================
// Mutation: Assign Plan to Company (Superadmin Only)
// ============================================================================

export interface AssignPlanParams {
  company_id: string;
  plan_slug: string;
  status?: 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid' | 'paused';
  is_grandfathered?: boolean;
  trial_end?: string | null;
  billing_cycle?: 'monthly' | 'yearly';
}

export function useAssignPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: AssignPlanParams): Promise<string> => {
      const { data, error } = await supabase.rpc('admin_assign_plan', {
        p_company_id: params.company_id,
        p_plan_slug: params.plan_slug,
        p_status: params.status || 'active',
        p_is_grandfathered: params.is_grandfathered || false,
        p_trial_end: params.trial_end || null,
        p_billing_cycle: params.billing_cycle || 'monthly',
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'company-subscriptions'] });
    },
  });
}

// ============================================================================
// Mutation: Delete Company (Superadmin Only)
// ============================================================================

/**
 * Permanently delete a company and all associated data (Super admin only)
 * This is IRREVERSIBLE — deletes all data, members, and auth users.
 */
export function useDeleteCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (companyId: string): Promise<{
      company_name: string;
      organizations_deleted: number;
      contacts_deleted: number;
      deals_deleted: number;
      activities_deleted: number;
      tasks_deleted: number;
      members_count: number;
      auth_users_deleted: number;
    }> => {
      const { data, error } = await supabase.rpc('admin_delete_company', {
        p_company_id: companyId,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin'] });
    },
  });
}

// ============================================================================
// Mutation: Revoke Company Subscription (Superadmin Only)
// ============================================================================

export function useRevokeSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (subscriptionId: string): Promise<boolean> => {
      const { data, error } = await supabase.rpc('admin_revoke_subscription', {
        p_subscription_id: subscriptionId,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'company-subscriptions'] });
    },
  });
}
